import React, { useState } from "react";
import { ChangePasswordPage } from "./ChangePasswordPage";
import { PasswordField } from "@/components/ui/PasswordField";
import { formatRuPhoneInput, normalizeToE164 } from "@/lib/phoneRu";

export interface AdminLoginResponseDTO {
    id: number;
    userId: string;
    phoneNumber: string;
    isTempPassword: boolean;
    locationId: number;
    adminBotId: number;
    clientBotId: number;
}

interface Props {
    onLoginSuccess: (user: AdminLoginResponseDTO) => void;
}

type Mode = "login" | "changePassword";

export const BrowserAuthPage: React.FC<Props> = ({ onLoginSuccess }) => {
    const [mode, setMode] = useState<Mode>("login");

    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const [showTempDialog, setShowTempDialog] = useState(false);
    const [tempUser, setTempUser] = useState<AdminLoginResponseDTO | null>(null);

    const handleLogin = async () => {
        setError(null);
        setLoading(true);

        try {
            const response = await fetch("/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ phoneNumber: normalizeToE164(phoneNumber), password }),
            });

            if (!response.ok) {
                setError("Ошибка авторизации. Проверьте номер и пароль.");
                return;
            }

            const userData: AdminLoginResponseDTO = await response.json();

            if (userData.phoneNumber) setPhoneNumber(formatRuPhoneInput(userData.phoneNumber));

            if (userData.isTempPassword) {
                setTempUser(userData);
                setShowTempDialog(true);
            } else {
                onLoginSuccess(userData);
            }
        } catch (e) {
            console.error(e);
            setError("Ошибка соединения с сервером.");
        } finally {
            setLoading(false);
        }
    };

    const handleChangePasswordLater = () => {
        if (tempUser) onLoginSuccess(tempUser);
        setShowTempDialog(false);
    };

    const handleChangePasswordNow = () => {
        setShowTempDialog(false);
        setMode("changePassword");
    };

    const reloginWithNewPassword = async (newPassword: string) => {
        setError(null);
        setLoading(true);

        try {
            const response = await fetch("/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ phoneNumber: normalizeToE164(phoneNumber), password: newPassword }),
            });

            if (!response.ok) {
                setError("Пароль сменили, но не удалось войти. Попробуйте войти вручную.");
                setMode("login");
                setPassword("");
                return;
            }

            const userData: AdminLoginResponseDTO = await response.json();
            onLoginSuccess(userData);
        } catch (e) {
            console.error(e);
            setError("Пароль сменили, но ошибка соединения при входе. Попробуйте войти вручную.");
            setMode("login");
            setPassword("");
        } finally {
            setLoading(false);
        }
    };

    if (mode === "changePassword") {
        return (
            <ChangePasswordPage
                phoneNumber={phoneNumber}
                oldPassword={password}
                onCancel={() => setMode("login")}
                onSuccess={(newPass) => void reloginWithNewPassword(newPass)}
            />
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-950 px-4">
            <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/80 p-6 shadow-xl">
                <h1 className="text-lg font-semibold text-slate-50 text-center mb-4">
                    Вход в панель теннисного клуба
                </h1>

                <div className="mb-3">
                    <label htmlFor="phoneNumber" className="block text-[11px] text-slate-300 mb-1">
                        Номер телефона
                    </label>
                    <input
                        id="phoneNumber"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(formatRuPhoneInput(e.target.value))}
                        placeholder="+7 (999) 999-99-99"
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 outline-none focus:border-sky-500"
                        autoComplete="username"
                    />
                </div>

                <div className="mb-3">
                    <PasswordField
                        label="Пароль"
                        value={password}
                        onChange={(v) => setPassword(v)}
                        placeholder="Введите пароль"
                        autoComplete="current-password"
                    />
                </div>

                {error && (
                    <div className="mb-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-[11px] text-red-200 text-center">
                        {error}
                    </div>
                )}

                <button
                    type="button"
                    disabled={loading}
                    onClick={() => void handleLogin()}
                    className="w-full rounded-xl bg-sky-500 py-2 text-xs font-medium text-white hover:bg-sky-600 disabled:opacity-60"
                >
                    {loading ? "Входим..." : "Войти"}
                </button>
            </div>

            {showTempDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 text-xs text-slate-100 shadow-2xl">
                        <h2 className="text-sm font-semibold mb-3">Смена временного пароля</h2>

                        <p className="mb-4 text-[11px] text-slate-200">
                            Ваш текущий пароль является временным. Хотите сменить его сейчас?
                        </p>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleChangePasswordLater}
                                className="rounded-xl border border-slate-600 px-3 py-1.5 text-[11px] text-slate-200 hover:bg-slate-800"
                            >
                                Сменить позже
                            </button>

                            <button
                                type="button"
                                onClick={handleChangePasswordNow}
                                className="rounded-xl bg-sky-500 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-sky-600"
                            >
                                Сменить сейчас
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
